import { z } from 'zod';

export const NOTIFICATION_FIELD_TYPES = Object.freeze([
    'string',
    'enum',
    'number',
    'date',
    'boolean',
]);

export const NOTIFICATION_CONDITION_OPERATORS = Object.freeze([
    'equals',
    'notEquals',
    'in',
    'notIn',
    'contains',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'between',
    'isSet',
    'isNotSet',
]);

export const NOTIFICATION_CONDITION_LIMITS = Object.freeze({
    maxDepth: 3,
    maxLeaves: 20,
    maxListItems: 20,
    maxStringLength: 256,
});

const fieldKeySchema = z.string()
    .min(1)
    .max(64)
    .regex(/^[a-z][A-Za-z0-9]*$/, 'notificationCondition.invalidFieldKey');

const scalarSchema = z.union([
    z.string().max(NOTIFICATION_CONDITION_LIMITS.maxStringLength),
    z.number().finite(),
    z.boolean(),
    z.null(),
]);

export const NotificationConditionValueSchema = z.union([
    scalarSchema,
    z.array(scalarSchema).max(NOTIFICATION_CONDITION_LIMITS.maxListItems),
]).optional();

export const NotificationConditionPredicateSchema = z.object({
    kind: z.literal('predicate'),
    field: fieldKeySchema,
    operator: z.enum(NOTIFICATION_CONDITION_OPERATORS),
    value: NotificationConditionValueSchema,
}).strict().superRefine((predicate, ctx) => {
    const noValue = predicate.operator === 'isSet' || predicate.operator === 'isNotSet';
    if (noValue && predicate.value !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'notificationCondition.valueForbidden',
        });
    }
    if (!noValue && predicate.value === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'notificationCondition.valueRequired',
        });
    }
    if ((predicate.operator === 'in' || predicate.operator === 'notIn' || predicate.operator === 'between')
        && !Array.isArray(predicate.value)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'notificationCondition.arrayValueRequired',
        });
    }
    if (predicate.operator === 'between'
        && Array.isArray(predicate.value)
        && predicate.value.length !== 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['value'],
            message: 'notificationCondition.betweenRequiresTwoValues',
        });
    }
});

export const NotificationConditionNodeSchema = z.lazy(() => z.union([
    NotificationConditionPredicateSchema,
    z.object({
        kind: z.literal('group'),
        operator: z.enum(['all', 'any']),
        nodes: z.array(NotificationConditionNodeSchema).max(NOTIFICATION_CONDITION_LIMITS.maxLeaves),
    }).strict(),
]));

export const NotificationConditionSchema = NotificationConditionNodeSchema.nullable().optional();

/**
 * Zod validates node shape. This explicit walker enforces aggregate depth and
 * leaf limits without accepting executable expressions or arbitrary paths.
 */
export function validateNotificationConditionLimits(condition, limits = NOTIFICATION_CONDITION_LIMITS) {
    if (condition == null) return { valid: true, depth: 0, leaves: 0, issues: [] };

    let maxDepth = 0;
    let leaves = 0;
    const issues = [];

    function visit(node, depth) {
        maxDepth = Math.max(maxDepth, depth);
        if (depth > limits.maxDepth) {
            issues.push({ code: 'CONDITION_DEPTH_EXCEEDED', depth, max: limits.maxDepth });
            return;
        }
        if (node.kind === 'predicate') {
            leaves += 1;
            if (leaves > limits.maxLeaves) {
                issues.push({ code: 'CONDITION_LEAF_LIMIT_EXCEEDED', leaves, max: limits.maxLeaves });
            }
            return;
        }
        for (const child of node.nodes) visit(child, depth + 1);
    }

    visit(condition, 1);
    return { valid: issues.length === 0, depth: maxDepth, leaves, issues };
}
